import React from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  FormHelperText,
} from "@mui/material";
import { Icon, StyledButton } from "components/common";
import { getExpectedTotalHours, sumScheduleWeekHours } from "./forms/scheduleWorkTimeValidation";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance } from "utils/helpers";
import { WintSuperkollAPIModelWintSalary } from "wint-api-client/dist/types";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

interface ScheduleProps {
  value?: WintSuperkollAPIModelWintSalary.ScheduleWeek[];
  onChange?: (value: WintSuperkollAPIModelWintSalary.ScheduleWeek[]) => void;
  disabled?: boolean;
  usualWorkHours?: number;
  employmentRate?: number;
  errorText?: string;
}

export const generateDefaultScheduleWeek = (
  usualWorkHours: number = 40,
  employmentRate: number = 100
): WintSuperkollAPIModelWintSalary.ScheduleWeek => {
  const adjustedHours = usualWorkHours * (employmentRate / 100);
  const dailyHours = Math.round((adjustedHours / 5) * 100) / 100;

  return {
    week: 0,
    monday: dailyHours,
    tuesday: dailyHours,
    wednesday: dailyHours,
    thursday: dailyHours,
    friday: dailyHours,
    saturday: 0,
    sunday: 0,
  };
};

const Schedule: React.FC<ScheduleProps> = ({
  value = [],
  onChange,
  disabled = false,
  usualWorkHours = 40,
  employmentRate = 100,
  errorText,
}) => {
  const [editingCell, setEditingCell] = React.useState<{ week: number; day: string; value: string } | null>(null);
  const parts = LocalizationHelperInstance.getNumberSeparators();

  const weekDays = [
    {
      key: "monday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_0),
    },
    {
      key: "tuesday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_1),
    },
    {
      key: "wednesday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_2),
    },
    {
      key: "thursday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_3),
    },
    {
      key: "friday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_4),
    },
    {
      key: "saturday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_5),
    },
    {
      key: "sunday",
      label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.WeekDayShortFromMonday_6),
    },
  ];

  const MAX_HOURS_PER_DAY = 24;

  const handleTimeChange = (weekNumber: number, dayKey: string, timeValue: string) => {
    if (disabled || !onChange) return;

    // Update editing cell state to preserve user input
    setEditingCell({ week: weekNumber, day: dayKey, value: timeValue });

    // Remove 'h' suffix and any non-numeric characters except decimal separators (. and ,)
    const cleanValue = timeValue.replace(/[^\d.,]/g, "");
    const normalizedValue = cleanValue.replace(",", ".");
    let numericValue = normalizedValue ? parseFloat(normalizedValue) || 0 : 0;

    // Limit to 2 decimal places
    numericValue = Math.round(numericValue * 100) / 100;
    if (numericValue > MAX_HOURS_PER_DAY) {
      numericValue = MAX_HOURS_PER_DAY;
    }

    const updatedRows = value.map((row) =>
      row.week === weekNumber
        ? {
            ...row,
            [dayKey]: numericValue,
          }
        : row
    );
    onChange(updatedRows);
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>, weekNumber: number, dayKey: string) => {
    const hours = value.find((r) => r.week === weekNumber)?.[
      dayKey as keyof WintSuperkollAPIModelWintSalary.ScheduleWeek
    ] as number;
    const displayValue = hours > 0 ? hours.toString().replace(".", parts.decimalSeparator || ".") : "";
    setEditingCell({ week: weekNumber, day: dayKey, value: displayValue });
    // Select all text when focusing to make editing easier
    setTimeout(() => {
      event.target.select();
    }, 0);
  };

  const handleInputBlur = (weekNumber: number, dayKey: string) => {
    setEditingCell(null);
  };

  const handleDeleteRow = (weekNumber: number) => {
    if (disabled || !onChange) return;

    // Remove the row and renumber remaining rows sequentially
    const filteredRows = value.filter((row) => row.week !== weekNumber);
    const renumberedRows = filteredRows.map((row, index) => ({
      ...row,
      week: index,
    }));

    onChange(renumberedRows);
  };

  const addNewRow = () => {
    if (disabled || !onChange) return;

    // Find the maximum week number and add 1 to avoid duplicates
    const maxWeekNumber = value.length > 0 ? Math.max(...value.map((w) => w.week)) : -1;
    const newWeekNumber = maxWeekNumber + 1;
    const newRow = { ...generateDefaultScheduleWeek(usualWorkHours, employmentRate), week: newWeekNumber };

    onChange([...value, newRow]);
  };

  // Add default row if schedule is empty
  React.useEffect(() => {
    if (value.length === 0 && !disabled && onChange) {
      addNewRow();
    }
  }, [value.length, disabled, onChange, usualWorkHours, employmentRate]);

  const calculateTotalHours = () => {
    if (value.length === 0) return 0;

    return Math.round(sumScheduleWeekHours(value) * 100) / 100;
  };

  const getDifferenceFromExpected = () => {
    return calculateTotalHours() - getExpectedTotalHours(value.length, usualWorkHours, employmentRate);
  };

  const getDifferenceText = () => {
    const difference = getDifferenceFromExpected();
    const sign = difference >= 0 ? "+" : "";
    return `${sign}${Math.round(difference * 100) / 100}h`;
  };

  const getDifferenceFromExpectedAbsolute = () => {
    return Math.abs(getDifferenceFromExpected());
  };

  return (
    <Grid container direction="column" spacing={2} className={cssExports.employeeScheduleContainer}>
      <Grid item className={cssExports.employeeScheduleTableWrapper}>
        <TableContainer
          component={Paper}
          elevation={0}
          className={clsx(cssExports.tableContainerOuter, cssExports.employeeScheduleTableContainer)}
        >
          <Table size="small" className={cssExports.tableInline}>
            <TableHead className={cssExports.tableHead}>
              <TableRow>
                <TableCell className={cssExports.tableCell}></TableCell>
                {weekDays.map((day) => (
                  <TableCell key={day.key} align="center" className={cssExports.tableCell}>
                    <span className={cssExports.formControlLabel}>{day.label}</span>
                  </TableCell>
                ))}
                {!disabled && <TableCell className={cssExports.tableCell}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody className={cssExports.tableBody}>
              {value.map((row) => (
                <TableRow key={row.week} className={clsx(cssExports.tableRow, cssExports.fieldRow)}>
                  <TableCell className={cssExports.tableCell}>
                    <span className={cssExports.tableListMainText}>
                      {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.WorkWeek)} {row.week + 1}
                    </span>
                  </TableCell>
                  {weekDays.map((day) => {
                    const hours = row[day.key as keyof WintSuperkollAPIModelWintSalary.ScheduleWeek] as number;
                    const isEditing = editingCell?.week === row.week && editingCell?.day === day.key;
                    const displayValue = isEditing
                      ? editingCell.value
                      : hours > 0
                        ? `${hours.toString().replace(".", parts.decimalSeparator || ".")}h`
                        : "";
                    return (
                      <TableCell key={day.key} align="center" className={cssExports.tableCell}>
                        <div className={cssExports.iconInputContainer}>
                          <input
                            type="text"
                            name={`${row.week}-${day.key}`}
                            value={displayValue}
                            onChange={(e) => handleTimeChange(row.week, day.key, e.target.value)}
                            onFocus={(e) => handleInputFocus(e, row.week, day.key)}
                            onBlur={() => handleInputBlur(row.week, day.key)}
                            placeholder="-"
                            disabled={disabled}
                            autoComplete="off"
                            style={{ boxSizing: "border-box" }}
                          />
                        </div>
                      </TableCell>
                    );
                  })}
                  {!disabled && (
                    <TableCell align="center" className={cssExports.tableCell}>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRow(row.week)}
                        className={clsx(cssExports.btnRound, cssExports.close, cssExports.xs)}
                      >
                        <Icon icon={IconDefinitionConstants.WTimes} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {!disabled && (
        <Grid item>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            className={cssExports.employeeScheduleButtonLabels}
          >
            <Grid item>
              <StyledButton
                size="sm"
                onClick={addNewRow}
                startIcon={<Icon icon={IconDefinitionConstants.WPlus} />}
                className={cssExports.button}
              >
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.AddWeek)}
              </StyledButton>
            </Grid>

            {value.length > 0 && (
              <Grid item>
                <div className={cssExports.formControlDescription}>
                  <div>
                    {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.EnteredTime)}:{" "}
                    <span className={cssExports.formControlLabel}>{calculateTotalHours()}h</span>
                  </div>
                  {getDifferenceFromExpectedAbsolute() > 0 && (
                    <div>
                      {LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Settings.DifferenceFromExpectedWorkTime
                      )}
                      :
                      <span className={clsx(cssExports.formControlLabel, cssExports.employeeScheduleDifferenceText)}>
                        {getDifferenceText()}
                      </span>
                    </div>
                  )}
                  {errorText && <FormHelperText error>{errorText}</FormHelperText>}
                </div>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default Schedule;
